import { types } from 'mobx-state-tree'

import { getRootStore } from '#state/models/getRootStore'

import { BaseModel } from './BaseModel'

export const OrgMember = BaseModel.named('OrgMember')
  .props({
    id: types.string,
    name: types.string,
    email: types.string,
    avatar: types.maybeNull(types.string),
    permissions: types.array(types.string),
    role: types.string,
    isRemoving: false,
    isChangingRole: false,
  })
  .views((self) => ({
    get isAdmin(): boolean {
      return self.role === 'admin'
    },
    get isContributor(): boolean {
      return self.role === 'contributor'
    },
    get isOwner(): boolean {
      return self.role === 'owner'
    },
  }))
  .actions((self) => ({
    setIsRemoving(value: boolean) {
      self.isRemoving = value
    },
    setIsChangingRole(value: boolean) {
      self.isChangingRole = value
    },
  }))
  // async actions
  .actions((self) => ({
    async remove() {
      const { currentOrg, doDebug } = getRootStore(self)
      if (!currentOrg) return
      self.setIsRemoving(true)

      try {
        await doDebug()
        await self.apiClient.removeUserFromOrg({
          orgId: currentOrg.id,
          userId: self.id,
        })
        self.trackEvent('SHOW_REMOVED')
        currentOrg.removeMember(self.id)
      } finally {
        self.setIsRemoving(false)
      }
    },

    async removeSelf() {
      const { currentOrg, doDebug } = getRootStore(self)
      if (!currentOrg) return
      self.setIsRemoving(true)
      try {
        await doDebug()
        await self.apiClient.removeSelfFromOrg({
          orgId: currentOrg.id,
        })
        self.trackEvent('SHOW_REMOVED_SELF')
      } finally {
        self.setIsRemoving(false)
      }
    },

    async promote() {
      const { currentOrg, doDebug } = getRootStore(self)
      if (!currentOrg) return
      self.setIsChangingRole(true)

      try {
        await doDebug()
        await self.apiClient.promoteAdmin({
          orgId: currentOrg.id,
          userId: self.id,
        })
        self.trackEvent('ADMIN_PROMOTED')
        currentOrg.patchMemberRole(self.id, 'admin')
      } finally {
        self.setIsChangingRole(false)
      }
    },

    async demote() {
      const { currentOrg, user, doDebug } = getRootStore(self)
      if (!currentOrg) return
      self.setIsChangingRole(true)

      try {
        await doDebug()
        await self.apiClient.demoteAdmin({
          orgId: currentOrg.id,
          userId: self.id,
        })
        self.trackEvent('ADMIN_DEMOTED')
        currentOrg.patchMemberRole(self.id, 'contributor')

        if (user.id === self.id) {
          user.selectedMembership?.setIsAdmin(false)
          currentOrg.setInvites([])
        }
      } finally {
        self.setIsChangingRole(false)
      }
    },
  }))
